<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-stock-alloc">
        <i class="far fa-clipboard-list"></i> Stock Allocations
      </Tab>
      <Tab tabid="tab-sheets-completed">
        <i class="far fa-ballot"></i> Completed Sheets
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-stock-alloc">
        <div class="row no-gutter">
          <div class="col-lg-6">
            <ContentTitle>Stock Allocations</ContentTitle>
            <ContentSubTitle>Showing current optimiser stock allocation levels for panels available for optimisation</ContentSubTitle>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12">
            <LoadingPanel :loading="loading" :loadingError="loadingError" />
            <StaticDataTable class="static-table-list" v-if="!loading && !loadingError">
              <template v-slot:header>
                <tr>
                  <th>Stock</th>
                  <th>Sheets On Hand</th>
                  <th><Tooltip label="Sheets currently allocated for optimisation" helpStyle>Allocated Sheets</Tooltip></th>
                  <th><Tooltip label="Additional sheets required to pack all the unallocated panels" helpStyle>Additional Sheets Required</Tooltip></th>
                  <th>Total Panels</th>
                  <th>Allocated Panels</th>
                  <th>Unallocated Panels</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="stockInfo of stockAllocationList" :key="stockInfo.stockId">
                  <td>
                    <LinkStock :name="stockInfo.stockName" :id="stockInfo.stockId" />
                  </td>
                  <td>{{stockInfo.sheetsAvailable}}</td>
                  <td>{{stockInfo.sheetsAllocated}}</td>
                  <td :class="[(stockInfo.unallocatedPanelRequiredSheetCount || 0) > 0 ? 'stats-bad' : '']">{{(stockInfo.unallocatedPanelRequiredSheetCount || 0) == 0 ? '-' : stockInfo.unallocatedPanelRequiredSheetCount}}</td>
                  <td>{{stockInfo.totalPanelCount}}</td>
                  <td>{{stockInfo.panelAllocatedCount}}</td>
                  <td>{{stockInfo.panelUnallocatedCount}}</td>          
                </tr>
              </template>
              <template v-slot:empty v-if="stockAllocationList.length == 0">No stock data available</template>
            </StaticDataTable>
          </div>
        </div>
      </TabPage>

      <TabPage tabid="tab-sheets-completed">
        <div class="row no-gutter">
          <div class="col-lg-6">
            <ContentTitle>Completed Sheets</ContentTitle>
            <ContentSubTitle>Showing optimiser data by stock for sheets completed</ContentSubTitle>
          </div>
        </div>

        <div class="row mt-2">
          <div class="col-12">
            <DataTable ref="dataTable" :columns="stockBreakdownColumnList" defaultColumnOrderDir="DESC" :defaultColumnOrderIndex="0" :url="stockBreakdownURL" :filterOptions="[]" :displayTools="true" :displayFilters="false" :hasDateRangeFilter="true" :displayExportTools="true" exportTitle="Optimiser Breakdown">
              <template v-slot:title>
                <slot name="title" />
              </template>
              <template v-slot:tools>
                <slot name="tools" />
              </template>
              <template v-slot:row="row">
                <tr :key="row.data.id">
                  <td class="table-data-nav-link">
                    <LinkStock :id="row.data.stockId" :name="row.data.stockName" />
                  </td>
                  <td>{{row.data.totalSheetCount}}</td>
                  <td>{{row.data.totalPanelCount}}</td>
                  <td>{{row.data.totalBlankPanelCount}}</td>
                  <td>{{row.data.totalOffcutCount}}</td>
                  <td>{{row.data.totalDefectCount}}</td>
                  <td>{{row.data.totalOrderCount}}</td>

                  <td>{{formatDecimal(row.data.totalSheetArea, 2)}}m&sup2;</td>
                  <td>{{formatPercent(row.data.totalUsedPercent)}}</td>
                  <td>{{formatPercent(row.data.totalOffcutPercent)}}</td>
                  <td>{{formatPercent(row.data.totalWastePercent)}}</td>
                  <td>{{formatPercent(row.data.totalDefectPercent)}}</td>
                </tr>
              </template>
            </DataTable>
          </div>
        </div>
      </TabPage>
      
    </template>
  </TabbedBox>
</template>


<script>
import _ from 'lodash'

import formMixin from '@/mixins/form'
import Utils from '@/utils/utils'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import { InventoryStockTypes } from '@/store/types'

export default {
  name: 'ProductionOptimiser',
  mixins: [formMixin],
  components: {
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup
  },
  data() {
    return {
      loading: false,
      loadingError: '',
      stockAllocationList: [],
      stockList: [],
      stockLookupList: [],

      stockBreakdownColumnList: [
        { title: "Stock", data: "stockName" },
        { title: "Sheets", data: "totalSheetCount" },
        { title: "Panels", data: "totalPanelCount" },
        { title: "Blank Panels", data: "totalBlankPanelCount" },
        { title: "Offcuts", data: "totalOffcutCount" },
        { title: "Defects", data: "totalDefectCount" },
        { title: "Orders", data: "totalOrderCount" },
        { title: "Total Area", data: "totalSheetArea" },
        { title: "Used Percent", data: "totalUsedPercent" },
        { title: "Offcut Percent", data: "totalOffcutPercent" },
        { title: "Waste Percent", data: "totalWastePercent" },
        { title: "Defect Percent", data: "totalDefectPercent" },
        { title: "Completed", data: "dateClosed", dateFilter: true, type: 'datetime', hidden: true, export: false },
      ]
    }
  },
  computed: {
    stockBreakdownURL() {
      var params = {
        filter: {}
      }

      return '/intelligence/production/optimiser/stock?' + $.param(params)
    },
  },
  mounted() {
    this.requestSummary()
    this.requestData()

    this.subscribeEndpoint('/production/sheetschedule', () => {
      this.reloadData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/production/sheetschedule')
  },
  methods: {
    reloadData() {
      this.requestSummary()
    },
    requestData() {
      this.$apiService.inventoryService.getStockList({}, { isActive: 1, type: InventoryStockTypes.glass })
      .then(response => {
        this.stockList = response.data
        this.stockLookupList = this.stockList.map(stock => {
          return {
            value: stock.id,
            name: `${stock.code} | ${stock.name}`
          }
        })
      })
    },
    requestSummary() {
      this.loading = true
      this.$apiService.analyticsService.getProductionOptimiserStockAllocations()
      .then(response => {
        this.stockAllocationList = response.data
      })
      .catch(err => {
        this.loadingError = this.processFormResponse(err).errorMessage
      })
      .finally(() => {
        this.loading = false
      })
    },
  }
}
</script>
